/**
 * Opt-in Shop experiments (On/Off after a high walker level).
 * Firestore field on users/{uid} — wire value unchanged: `experiments`.
 * Level-gated rewards (no switch) live in ./levelUnlocks.js.
 */

import { doc, serverTimestamp, updateDoc } from 'firebase/firestore';
import { db } from '../../firebase';
import { USERS_COLLECTION } from './userProfile';
import { levelFromProfile } from './userLevels';

export const EXPERIMENT_INTENT_SEGMENT = 'intent-segment';
export const EXPERIMENT_MAP_ICONS = 'map-icons';
export const EXPERIMENT_EVENTS_MAP = 'events-map';

/** Default lab unlock — Host band, above merch (Lv 7), below Legend. */
export const EXPERIMENT_UNLOCK_LEVEL = 8;

export const EXPERIMENTS = Object.freeze([
  {
    id: EXPERIMENT_INTENT_SEGMENT,
    title: 'What do you want?',
    blurb: 'Six intent cards on the home page — pick a mood, get the matching events or places.',
    glyph: '?',
    level: EXPERIMENT_UNLOCK_LEVEL,
    openTo: { href: '/', label: 'Open Home' },
  },
  {
    id: EXPERIMENT_MAP_ICONS,
    title: 'Meaningful map icons',
    blurb: 'Place pins show their category glyph; drops take a shape by rarity.',
    glyph: '☕',
    level: EXPERIMENT_UNLOCK_LEVEL,
    openTo: { href: '/places?view=map', label: 'Open Places map' },
  },
  {
    id: EXPERIMENT_EVENTS_MAP,
    title: 'Events on the map',
    blurb: 'A second page on Events: today, this week, this month, or all upcoming, on the map.',
    glyph: '⌖',
    level: 2,
    openTo: { href: '/?view=map#events', label: 'Open map' },
  },
]);

const EXPERIMENT_BY_ID = new Map(EXPERIMENTS.map((item) => [item.id, item]));

export function experimentForId(id) {
  return EXPERIMENT_BY_ID.get(id) || null;
}

export function isExperimentUnlocked(profile, id) {
  const item = experimentForId(id);
  if (!item) return false;
  const required = Number(item.level) || EXPERIMENT_UNLOCK_LEVEL;
  return levelFromProfile(profile).level >= required;
}

export function isExperimentEnabled(profile, id) {
  if (!isExperimentUnlocked(profile, id)) return false;
  return Boolean(profile?.experiments?.[id]);
}

export async function setExperimentEnabled(uid, id, on, profile) {
  if (!uid) throw new Error('Missing uid.');
  if (!experimentForId(id)) throw new Error('Unknown experiment.');
  if (on && !isExperimentUnlocked(profile, id)) {
    const required = Number(experimentForId(id)?.level) || EXPERIMENT_UNLOCK_LEVEL;
    throw new Error(`Reach Lv ${required} to turn this on.`);
  }
  await updateDoc(doc(db, USERS_COLLECTION, uid), {
    [`experiments.${id}`]: Boolean(on),
    updatedAt: serverTimestamp(),
  });
}
